import { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { addLead } from '../../api/apiService';
import { SimCardDownloadOutlined, UploadFileOutlined } from '@mui/icons-material';

const LeadUploadModal = ({ open, onClose, clientId, fetchLeads }) => {
  const [validLeads, setValidLeads] = useState([]);
  const [invalidLeads, setInvalidLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const headerMap = {
    firstname: 'firstName',
    lastname: 'lastName',
    email: 'email',
    company: 'company',
    jobtitle: 'jobTitle',
    industry: 'industry',
    companysize: 'companySize',
    website: 'website',
    phone: 'phone',
    linkedin: 'linkedin',
    country: 'country',
    state: 'state',
    city: 'city',
    // status: 'status',
    // score: 'score',
    // tags: 'tags',
  };

  const requiredFields = [
    'firstName',
     'lastName',
      'email', 
      'company',
      'jobTitle',
      'industry',
      'companySize',
      'website',
      'phone',
      'linkedin',
      'country',
      'state',
      'city',
    ];

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        const [headers, ...rows] = raw;
        const cleanHeaders = headers.map((h) => h.trim().toLowerCase());

        const parsedValid = [];
        const parsedInvalid = [];

        rows.forEach((row, i) => {
          const isEmptyRow = row.every(cell => cell === '' || cell === null || typeof cell === 'undefined');
          if (isEmptyRow) return;

          const lead = {};
          cleanHeaders.forEach((header, index) => {
            const mappedKey = headerMap[header];
            if (mappedKey) {
              lead[mappedKey] = typeof row[index] === 'string' ? row[index].trim() : row[index];
            }
          });

          lead.tags = typeof lead.tags === 'string'
            ? lead.tags.split(/[,;]/).map((t) => t.trim()).slice(0, 5)
            : [];

          lead.status = lead.status || 'new';

          const missing = requiredFields.filter((key) => !lead[key]);
          if (missing.length) {
            parsedInvalid.push({ ...lead, __reason: `Missing: ${missing.join(', ')}`, id: i });
          } else {
            parsedValid.push({ ...lead, id: i });
          }
        });

        setValidLeads(parsedValid);
        setInvalidLeads(parsedInvalid);
      } catch (error) {
        console.error('Parsing error:', error);
        showSnackbar('Failed to parse file.', 'error');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    if (validLeads.length === 0) {
      showSnackbar('No valid leads to submit.', 'info');
      return;
    }

    setLoading(true);
    let success = 0;
    let failed = 0;

    for (const lead of validLeads) {
      try {
        const payload = { ...lead, clientId };
        await addLead(payload);
        success++;
      } catch (err) {
        console.error('Upload failed for lead:', lead);
        failed++;
      }
    }

    setLoading(false);

    if (success > 0) showSnackbar(`${success} lead(s) uploaded successfully.`, 'success');
    if (failed > 0) showSnackbar(`${failed} lead(s) failed to upload / Email already exist.`, 'error');

    if (typeof fetchLeads === 'function') fetchLeads();
    onClose();
  };

  /*Upload template download as xslsx */
  const handleUploadTemplate = () => {
    const exportData = [{
    FirstName: "",
    LastName: "",
    Email: "",
    Company: "",
    JobTitle: "",
    Industry: "",
    CompanySize: "",
    Website: "",
    Phone: "",
    LinkedIn: "",
    Country: "",
    State: "",
    City: "",
    // Status: "",
    //Score: "",
    //Tags: "",
    }];
    
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'LeadsTemplate');

  XLSX.writeFile(workbook, 'leads_template.xlsx');
  }

  const columns = Object.values(headerMap).map((key) => ({ field: key, headerName: key, flex: 1 }));

  return (
    <>
      {/* File upload form dailog */}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" 
        slotProps={{ paper :{
          sx: {
            borderRadius: 2,  
          },
        }
        }}
      >
        <DialogTitle>Upload Leads</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            {/* Download Template */}
            <Grid sx={{display:'flex', justifyContent:'flex-start'}} gap={2} size={{xs:6, sm:'auto'}}>
              <Button 
                size="medium"
                variant="outlined"
                startIcon={<SimCardDownloadOutlined />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={handleUploadTemplate}
              >
                Download Sample Template
              </Button>
            <Button variant="outlined" startIcon={<UploadFileOutlined />} component="label" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Upload Excel / CSV
              <input type="file" hidden onChange={handleFileChange} accept=".xlsx, .csv" />
            </Button>
            </Grid>
          </Box>

          {validLeads.length > 0 && (
            <>
              <Typography variant="h6">Valid Leads ({validLeads.length})</Typography>
              <Box height={300}>
                <DataGrid rows={validLeads} columns={columns} pageSize={5} />
              </Box>
            </>
          )}

          {invalidLeads.length > 0 && (
            <>
              <Typography variant="h6" color="error">Invalid Leads ({invalidLeads.length})</Typography>
              <Box height={300}>
                <DataGrid
                  rows={invalidLeads}
                  columns={[
                    ...columns,
                    {
                      field: '__reason',
                      headerName: 'Reason',
                      width: 300,
                      renderCell: (params) => (
                      <Box
                      sx={{
                          display: 'flex',
                          width: '100%',
                          height: '100%',
                          whiteSpace: 'normal',   // allow wrapping
                          wordBreak: 'break-word', // wrap long words like emails
                          overflowWrap: 'break-word',
                          py:0
                      }}
                      >
                          <Typography color="error">{params.value}</Typography>
                      </Box>
                       // <Typography color="error">{params.value}</Typography>
                      ),
                    },
                  ]}
                  pageSize={5}
                />
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={validLeads.length === 0 || loading}
          >
            {loading ? 'Uploading...' : 'Submit Valid Leads'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LeadUploadModal;
